/**
 * Session Management Service
 * 
 * Handles multi-device session management:
 * - Force logout previous sessions
 * - Cancel incomplete new login
 * - Session invalidation signaling
 * 
 * Security: All operations are user-restricted via Firebase rules
 */

import { doc, setDoc, serverTimestamp, signOut } from 'firebase/firestore';
import { signOut as firebaseSignOut } from 'firebase/auth';
import { db, auth } from '../firebase';

/**
 * Force logout all previous sessions for a user
 * Called when user logs in from a new device and chooses to logout previous sessions
 * 
 * @param {string} userId - Firebase user ID
 * @param {string} newSessionId - The new session ID to keep active
 * @returns {Promise<void>}
 */
export const forceLogoutPreviousSessions = async (userId, newSessionId) => {
  try {
    if (!userId || !newSessionId) {
      throw new Error('Missing userId or newSessionId');
    }

    // Update the activeSessions document with new session ID
    // This triggers the real-time listener on old devices to detect mismatch
    await setDoc(doc(db, 'activeSessions', userId), {
      sessionId: newSessionId,
      deviceInfo: {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        timestamp: serverTimestamp()
      },
      loginTime: serverTimestamp(),
      lastActive: serverTimestamp(),
      // Mark that this is a forced logout event
      forceLogoutTriggered: true,
      forceLogoutAt: serverTimestamp()
    }, { merge: true });

    console.log('✅ Force logout signal sent to previous sessions');
  } catch (error) {
    console.error('Error forcing logout of previous sessions:', error);
    throw new Error(`Failed to logout previous sessions: ${error.message}`);
  }
};

/**
 * Cancel new login - sign out the user trying to log in from new device
 * Called when user clicks "Cancel" on the new device login dialog
 * 
 * @returns {Promise<void>}
 */
export const cancelNewLogin = async () => {
  try {
    // Clear session data from localStorage
    localStorage.removeItem('sessionId');

    // Clear device-related data
    sessionStorage.clear();

    // Sign out from Firebase
    await firebaseSignOut(auth);

    console.log('✅ New login cancelled - session cleared');
  } catch (error) {
    console.error('Error canceling new login:', error);
    throw new Error(`Failed to cancel login: ${error.message}`);
  }
};

/**
 * Validate that session is still active (hasn't been invalidated)
 * Used to check if current session is still valid after detecting a force logout
 * 
 * @param {string} userId - Firebase user ID
 * @param {string} currentSessionId - Current session ID to validate
 * @returns {Promise<boolean>} - true if session is valid, false if invalidated
 */
export const validateSessionActive = async (userId, currentSessionId) => {
  try {
    if (!userId || !currentSessionId) {
      return false;
    }

    const { getDoc } = await import('firebase/firestore');
    const sessionRef = doc(db, 'activeSessions', userId);
    const sessionSnap = await getDoc(sessionRef);

    if (!sessionSnap.exists()) {
      return false;
    }

    const sessionData = sessionSnap.data();

    // If session IDs match, session is still active
    return sessionData.sessionId === currentSessionId;
  } catch (error) {
    console.error('Error validating session:', error);
    return false;
  }
};

/**
 * Get current active session info
 * Useful for debugging and session monitoring
 * 
 * NOW WITH STALE SESSION DETECTION:
 * - Returns null if sessionId is null (user logged out)
 * - Returns null if lastActive is > 5 minutes old (session expired/stale)
 * - Only returns session data for truly active sessions
 * 
 * @param {string} userId - Firebase user ID
 * @returns {Promise<Object|null>} - Session data or null if no active session
 */
export const getActiveSessionInfo = async (userId) => {
  try {
    if (!userId) {
      return null;
    }

    const { getDoc } = await import('firebase/firestore');
    const sessionRef = doc(db, 'activeSessions', userId);
    const sessionSnap = await getDoc(sessionRef);

    if (!sessionSnap.exists()) {
      return null;
    }

    const sessionData = sessionSnap.data();

    // ✅ FIX: Check if sessionId is null (user logged out)
    if (!sessionData.sessionId) {
      console.log('📝 Session exists but sessionId is null (logged out)');
      return null;
    }

    // ✅ FIX: Check if session is stale (lastActive > 5 minutes ago)
    // This prevents treating old/abandoned sessions as active
    if (sessionData.lastActive && sessionData.lastActive.toMillis) {
      const lastActiveTime = sessionData.lastActive.toMillis();
      const currentTime = Date.now();
      const fiveMinutesInMs = 5 * 60 * 1000;

      if (currentTime - lastActiveTime > fiveMinutesInMs) {
        console.log('📝 Session is stale (lastActive > 5 minutes ago)');
        return null;
      }
    }

    return sessionData;
  } catch (error) {
    console.error('Error getting active session info:', error);
    return null;
  }
};

/**
 * Clear session on logout
 * Called when user explicitly logs out
 * 
 * @param {string} userId - Firebase user ID
 * @returns {Promise<void>}
 */
export const clearSessionOnLogout = async (userId) => {
  try {
    if (!userId) {
      return;
    }

    // Clear from localStorage
    localStorage.removeItem('sessionId');
    sessionStorage.clear();

    // Optionally, delete the session document (set to null)
    await setDoc(doc(db, 'activeSessions', userId), {
      sessionId: null,
      logoutAt: serverTimestamp()
    }, { merge: true });

    console.log('✅ Session cleared on logout');
  } catch (error) {
    console.error('Error clearing session on logout:', error);
    // Non-critical, don't throw
  }
};
